// Habilitations nominatives (D-023) : accès aux domaines sensibles et missions, avec règle des
// quatre yeux. Liste reconstituée depuis le journal d'audit (aucune route de liste en L0-A).
import { DOMAINES, ZONES_HABILITATION } from '@cc/contrats';
import { Bandeau, Bouton, CaseACocher, ConfirmationIntegree, DateChamp, EtatVide, Formulaire, GrilleChamps, Panneau, PuceEtat, Selection, Tableau, ZoneTexte, type ColonneTableau } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../api/client';
import { nomAppartenance, useUtilisateurs } from '../../api/requetes';
import type { EntreeAudit, Habilitation, Liste } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, versIso } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { LIBELLES_DOMAINES, peut } from '../../session/permissions';
import { LIBELLES_ZONES, reconstituerHabilitations, type HabilitationReconstituee } from './habilitations';
import styles from './administration.module.css';
import { nomUtilisateur } from './PageUtilisateurs';

function FormulaireHabilitation({ onTermine }: { onTermine: () => void }) {
  const utilisateurs = useUtilisateurs();
  const [appartenance, setAppartenance] = useState('');
  const [zone, setZone] = useState<string>('personnel_sensible');
  const [domaines, setDomaines] = useState<string[]>([]);
  const [motif, setMotif] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const creation = useAction({
    executer: () =>
      requete<Habilitation>('/habilitations', {
        methode: 'POST',
        corps: {
          appartenance_id: appartenance,
          zone,
          perimetre: zone === 'mission' ? { domaines } : {},
          motif,
          debut: debut ? versIso(debut) : '',
          fin: fin ? versIso(fin) : '',
        },
      }),
    succes: 'Habilitation enregistrée en attente de validation par une autre personne.',
    invalider: [['audit']],
    apres: onTermine,
  });
  const ids: Record<string, string> = { appartenance_id: 'hab-personne', zone: 'hab-zone', motif: 'hab-motif', debut: 'hab-debut', fin: 'hab-fin', perimetre: 'hab-domaines' };
  const erreur = (c: string) => creation.erreursChamps.find((e) => e.champ === c || e.champ.startsWith(`${c}.`))?.message ?? null;
  return (
    <Formulaire
      libelle="Nouvelle habilitation"
      onSoumettre={() => creation.lancer(undefined)}
      erreurs={creation.erreursChamps.map((e) => ({ champ: ids[e.champ.split('.')[0] ?? ''] ?? 'hab-personne', message: e.message }))}
      actions={
        <>
          <Bouton type="submit" chargement={creation.enCours}>
            Enregistrer l&apos;habilitation
          </Bouton>
          <Bouton variante="secondaire" onClick={onTermine}>
            Annuler
          </Bouton>
        </>
      }
    >
      {creation.messageErreur && creation.erreursChamps.length === 0 ? <Bandeau type="erreur">{creation.messageErreur}</Bandeau> : null}
      <GrilleChamps>
        <Selection
          id="hab-personne"
          libelle="Personne"
          optionVide="Choisir une personne"
          value={appartenance}
          onChange={(e) => setAppartenance(e.target.value)}
          options={(utilisateurs.data ?? []).filter((u) => u.statut_appartenance !== 'revoquee').map((u) => ({ valeur: u.appartenance_id, libelle: `${nomUtilisateur(u)} (${u.identifiant})` }))}
          erreur={erreur('appartenance_id')}
          required
        />
        <Selection id="hab-zone" libelle="Zone" value={zone} onChange={(e) => setZone(e.target.value)} options={ZONES_HABILITATION.map((z) => ({ valeur: z, libelle: LIBELLES_ZONES[z] ?? z }))} required />
        <DateChamp id="hab-debut" libelle="Début" avecHeure value={debut} onChange={(e) => setDebut(e.target.value)} erreur={erreur('debut')} required />
        <DateChamp id="hab-fin" libelle="Fin" avecHeure value={fin} onChange={(e) => setFin(e.target.value)} erreur={erreur('fin')} required />
      </GrilleChamps>
      {zone === 'mission' ? (
        <fieldset id="hab-domaines" className={styles.matrice}>
          <legend>Domaines de la mission (lecture et export)</legend>
          {erreur('perimetre') ? <Bandeau type="erreur">{erreur('perimetre')}</Bandeau> : null}
          <div className={styles.cases}>
            {DOMAINES.filter((d) => d !== 'supervision').map((d) => (
              <CaseACocher
                key={d}
                libelle={LIBELLES_DOMAINES[d] ?? d}
                checked={domaines.includes(d)}
                onChange={() => setDomaines((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]))}
              />
            ))}
          </div>
        </fieldset>
      ) : null}
      <ZoneTexte id="hab-motif" libelle="Motif" aide="Raison de l'accès, conservée au journal" value={motif} onChange={(e) => setMotif(e.target.value)} erreur={erreur('motif')} required />
    </Formulaire>
  );
}

export function PageHabilitations() {
  const { contexte } = useSession();
  const administrer = peut(contexte, 'utilisateurs', 'administrer');
  const valider = peut(contexte, 'utilisateurs', 'valider');
  const lireJournal = peut(contexte, 'audit', 'voir');
  const utilisateurs = useUtilisateurs();
  const [creation, setCreation] = useState(false);
  const journal = useQuery({
    queryKey: ['audit', 'habilitation'],
    queryFn: ({ signal }) => requete<Liste<EntreeAudit>>('/audit', { signal, parametres: { objet_type: 'habilitation', limite: 500 } }).then((r) => reconstituerHabilitations(r.elements)),
    enabled: lireJournal,
  });
  const validation = useAction({
    executer: (id: string) => requete<Habilitation>(`/habilitations/${id}/valider`, { methode: 'POST' }),
    succes: 'Habilitation validée : elle est effective sur sa période.',
    invalider: [['audit']],
  });
  const revocation = useAction({
    executer: (id: string) => requete<Habilitation>(`/habilitations/${id}`, { methode: 'DELETE' }),
    succes: 'Habilitation révoquée.',
    invalider: [['audit']],
  });

  const colonnes: ColonneTableau<HabilitationReconstituee>[] = [
    { id: 'personne', entete: 'Personne', enTeteLigne: true, masquable: false, cellule: (h) => (h.appartenance_id ? nomAppartenance(utilisateurs.data, h.appartenance_id) : 'non renseigné') },
    { id: 'zone', entete: 'Zone', cellule: (h) => `${LIBELLES_ZONES[h.zone] ?? h.zone}${h.domaines.length ? ` : ${h.domaines.map((d) => LIBELLES_DOMAINES[d] ?? d).join(', ')}` : ''}` },
    { id: 'periode', entete: 'Période', cellule: (h) => `du ${date(h.debut)} au ${date(h.fin)}`, valeurTri: (h) => h.debut },
    {
      id: 'statut',
      entete: 'État',
      valeurTri: (h) => h.statut,
      cellule: (h) =>
        h.statut === 'validee' ? <PuceEtat ton="ok" libelle="Validée" /> : h.statut === 'revoquee' ? <PuceEtat ton="neutre" libelle="Révoquée" /> : <PuceEtat ton="attente" libelle="En attente de validation" />,
    },
    { id: 'motif', entete: 'Motif', cellule: (h) => h.motif ?? 'non renseigné' },
    {
      id: 'actions',
      entete: 'Actions',
      masquable: false,
      cellule: (h) => (
        <div className={styles.cases} style={{ gridTemplateColumns: 'none', gap: 6 }}>
          {h.statut === 'en_attente_validation' && valider ? (
            <Bouton petit chargement={validation.enCours} onClick={() => validation.lancer(h.id)}>
              Valider
            </Bouton>
          ) : null}
          {h.statut !== 'revoquee' && administrer ? (
            <ConfirmationIntegree petit libelle="Révoquer" question="Révoquer cette habilitation ? L'accès cesse immédiatement." libelleConfirmer="Confirmer la révocation" enCours={revocation.enCours} onConfirmer={() => revocation.lancer(h.id)} />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Habilitations nominatives"
        description="Accès aux données sensibles (personnel, action sociale, réclamations) et missions de contrôle. Toute habilitation n'a d'effet qu'après validation par une seconde personne."
        actions={administrer && !creation ? <Bouton icone={Plus} onClick={() => setCreation(true)}>Nouvelle habilitation</Bouton> : null}
      />
      {creation ? (
        <Panneau titre="Nouvelle habilitation">
          <FormulaireHabilitation onTermine={() => setCreation(false)} />
        </Panneau>
      ) : null}
      {validation.messageErreur || revocation.messageErreur ? <Bandeau type="erreur">{validation.messageErreur ?? revocation.messageErreur}</Bandeau> : null}
      {!lireJournal ? (
        <EtatVide titre="Liste non disponible avec vos droits" icone={KeyRound}>
          <p>
            L&apos;API ne fournit pas encore de liste des habilitations ; elle est reconstituée à partir du journal des opérations, que votre rôle ne
            permet pas de consulter.
          </p>
        </EtatVide>
      ) : (
        <EtatRequete chargement={journal.isPending} erreur={journal.error} onReessayer={() => void journal.refetch()} fonction="Le journal des habilitations">
          <p className={styles.listePermissions} style={{ paddingLeft: 0 }}>
            Liste reconstituée à partir du journal des opérations (création, validation, révocation).
          </p>
          {(journal.data ?? []).length === 0 ? (
            <EtatVide titre="Aucune habilitation" icone={KeyRound}>
              <p>Aucune habilitation nominative n&apos;a été accordée dans cette collectivité.</p>
            </EtatVide>
          ) : (
            <Tableau legende="Habilitations nominatives" colonnes={colonnes} lignes={journal.data ?? []} cleLigne={(h) => h.id} />
          )}
        </EtatRequete>
      )}
    </>
  );
}
